import React from 'react';
import { useLocation } from 'react-router-dom';
import { AccessDeniedContent } from '../pages/AccessDenied';
import { NotEntitled } from '../pages/NotEntitled';
import { useAppRoles } from '../hooks/useAppRoles';
import { useRouteAccess } from '../hooks/useRouteAccess';
import { findRouteByPath } from '../constants/appRoutes';
import type { AppRole } from '../constants/appRoles';

interface RoleGuardProps {
  /** Require exactly this one role. */
  role?: AppRole;
  /** Require at least one of these roles (OR logic). */
  roles?: AppRole[];
  children: React.ReactNode;
}

/**
 * Renders children only when the current user holds the required app role(s).
 *
 * On failure it renders in place, without navigating away, so bookmarked,
 * shared or hand-typed URLs keep the attempted address while the restricted page itself is never rendered:
 *  - a user with no app roles at all is not entitled to the app and gets the AccessDenied message,
 *  - a user with roles but not the required ones gets the NotEntitled entitlement page.
 */
const RoleGuard: React.FC<RoleGuardProps> = ({ role, roles, children }) => {
  const { roles: userRoles, hasRole, hasAnyRole } = useAppRoles();
  const { suggestedRoute } = useRouteAccess();
  const location = useLocation();

  const registryRoute = findRouteByPath(location.pathname);
  const requiredRoles = roles ?? registryRoute?.roles;

  const allowed = role ? hasRole(role) : requiredRoles ? hasAnyRole(requiredRoles) : true;

  if (allowed) {
    return <>{children}</>;
  }

  if (userRoles.length === 0) {
    return <AccessDeniedContent />;
  }

  const suggested = suggestedRoute(location.pathname);

  return (
    <NotEntitled
      area={registryRoute?.group}
      suggested={suggested ? { path: suggested.path, label: suggested.label } : undefined}
    />
  );
};

export default RoleGuard;
